import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  inject,
  Type,
} from '@angular/core';
import { Partner, TableColumn } from '@app/shared';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Validators } from '@angular/forms';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { ScreenService, GlobalValuesService, getPartnerDisplayName } from '@app/core';
import { combineLatest, take } from 'rxjs';
import { MaestrosUtilsService } from '../../../features/maestros/services/maestros-utils.service';

@Component({
  selector: 'app-avatar-select',
  standalone: false,

  templateUrl: './avatar-select.component.html',
  styleUrl: './avatar-select.component.less',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AvatarSelectComponent),
      multi: true,
    },
  ],
})
export class AvatarSelectComponent {
  @Input() options: any[] = [];
  @Input() urlProperty: string = 'avatar';
  @Input() labelProperties: string[] = ['name', 'surName'];
  @Input() placeholder = 'Selecciona una opción...';
  @Input() showPartnerIcon = true;
  @Input() isCurrency = false;
  @Input() allowCreateNew = false; // Valor por defecto false
  @Input() createPartnerDrawerComponent?: Type<any>; // Componente del drawer para crear partner (opcional)
  @Input() defaultParentId?: number | null; // ID de empresa relacionada para prellenar al crear nuevo contacto
  @Input() defaultIsEmployee?: boolean = false; // Marcar como empleado por defecto al crear nuevo contacto
  @Input() nzAllowClear: boolean = true; // Permitir limpiar selección
  @Input() nzStatus?: 'error' | 'warning' | ''; // Estado del select (para errores)
  @Input() nzLoading: boolean = false; // Estado de carga
  @Output() searchChange = new EventEmitter<string>();
  @Output() partnerCreated = new EventEmitter<Partner>(); // Evento cuando se crea un partner

  private drawerService = inject(NzDrawerService);
  private screenService = inject(ScreenService);
  private globalValuesService = inject(GlobalValuesService);
  private maestrosUtilsService = inject(MaestrosUtilsService);

  value: number | null = null;
  disabled = false;
  searchValue: string = ''; // Almacenar el valor de búsqueda

  // Propiedad computada para la parte variable del texto de crear nuevo
  createNewSearchText: string = 'contacto...';

  onChange = (value: number | null) => {};
  onTouched = () => {};

  writeValue(value: number | null): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(value: any): void {
    // Si se selecciona la opción de crear nuevo
    if (value === 'CREATE_NEW' || value === 'create_new' || String(value) === 'CREATE_NEW') {
      // Resetear el valor para que el select no quede con 'CREATE_NEW' seleccionado
      setTimeout(() => {
        this.value = null;
      }, 0);
      this.openCreatePartnerDrawer(this.searchValue);
      // No cambiar el valor, solo abrir el drawer
      return;
    }

    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  // Método para manejar el click directo en la opción de crear nuevo
  onCreateNewClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openCreatePartnerDrawer(this.searchValue);
    // Cerrar el dropdown manualmente
    const selectElement = (event.target as HTMLElement).closest('nz-select');
    if (selectElement) {
      // Forzar el cierre del select
      setTimeout(() => {
        this.value = null;
      }, 0);
    }
  }

  onSearch(searchValue: string): void {
    this.searchValue = searchValue;
    this.updateCreateNewText();
    this.searchChange.emit(searchValue);
  }

  // Actualizar el texto de crear nuevo basado en el valor de búsqueda
  private updateCreateNewText(): void {
    if (!this.searchValue || this.searchValue.trim() === '') {
      this.createNewSearchText = 'contacto...';
    } else {
      this.createNewSearchText = `"${this.searchValue}"...`;
    }
  }

  getPartner(id: number): Partner {
    const optionList: any[] = this.options || [];
    return optionList.find((p) => p.id === id)!;
  }

  getOptionLabel(item: any): string {
    if (!item) return '';
    if (this.labelProperties[0] === 'name' && this.labelProperties[1] === 'surName') {
      return getPartnerDisplayName(item);
    }
    return this.labelProperties
      .map((labelProperty) => item[labelProperty])
      .filter(Boolean)
      .join(' ');
  }

  // Abrir el drawer para crear un nuevo partner
  openCreatePartnerDrawer(searchText: string): void {
    // Si no se proporciona el componente del drawer, no hacer nada
    if (!this.createPartnerDrawerComponent) {
      console.warn('createPartnerDrawerComponent no está definido. No se puede crear un nuevo partner.');
      console.warn('Por favor, proporciona [createPartnerDrawerComponent]="ModifyPartnerDrawerComponent" en el componente padre.');
      return;
    }

    // Construir las columnas completas con opciones
    this.buildCompletePartnerColumns().then((columnsData) => {
      this.openDrawerWithColumns(searchText, columnsData);
    });
  }

  private openDrawerWithColumns(searchText: string, columnsData: TableColumn[]): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe({
        next: ({ isMobile, isMedium }) => {
          try {
            const drawerRef = this.drawerService.create({
              nzWidth: 793,
              nzHeight: '98%',
              nzPlacement: isMobile ? 'bottom' : 'right',
              nzClosable: false,
              nzMaskClosable: false,
              nzMask: true,
              nzContent: this.createPartnerDrawerComponent!,
              nzData: {
                columnsData: columnsData,
                editingElement: null, // null para crear nuevo
                maestroName: 'Contactos',
              },
            });

            // Configurar drawerRef$ en MaestrosUtilsService para que el componente interno pueda cerrarlo
            this.maestrosUtilsService.drawerRef = drawerRef;

            // Escuchar cuando se abre el drawer para prellenar el formulario
            drawerRef.afterOpen.pipe(take(1)).subscribe({
              next: () => {
                try {
                  const component = drawerRef.getContentComponent();
                  // Si hay texto de b?squeda, prellenar el nombre completo sin inventar apellido.
                  if (searchText && searchText.trim() && component?.partnerForm) {
                    component.partnerForm.patchValue({
                      name: searchText.trim(),
                      surName: '',
                    });
                  }

                  // Prellenar parentId y employee si están disponibles
                  if (component?.partnerForm) {
                    const patchValues: any = {};
                    
                    if (this.defaultParentId) {
                      patchValues.parentId = this.defaultParentId;
                    }
                    
                    if (this.defaultIsEmployee) {
                      patchValues.employee = true;
                    }
                    
                    if (Object.keys(patchValues).length > 0) {
                      component.partnerForm.patchValue(patchValues);
                    }
                  }

                  // Escuchar el evento de creación
                  if (component?.createEvent) {
                    component.createEvent.pipe(take(1)).subscribe({
                      next: (newPartner: Partner) => {
                        // Agregar el nuevo partner a la lista de opciones
                        this.options.push(newPartner);

                        if (this.globalValuesService.partnerlist &&
                            !this.globalValuesService.partnerlist.find(p => p.id === newPartner.id)) {
                          this.globalValuesService.partnerlist.push(newPartner);
                        }

                        // Seleccionar el nuevo partner
                        this.value = newPartner.id;
                        this.onChange(newPartner.id);
                        this.onTouched();
                        // Emitir evento para que el padre pueda actualizar su lista
                        this.partnerCreated.emit(newPartner);
                        // Limpiar drawerRef$ antes de cerrar
                        this.maestrosUtilsService.drawerRef = undefined;
                        // Cerrar el drawer
                        drawerRef.close();
                      },
                      error: (error: any) => {
                        console.error('Error al crear partner:', error);
                        // Limpiar drawerRef$ en caso de error también
                        this.maestrosUtilsService.drawerRef = undefined;
                      }
                    });
                  } else {
                    console.warn('El componente del drawer no tiene createEvent');
                  }

                  // Escuchar cuando el drawer se cierre para limpiar drawerRef$
                  drawerRef.afterClose.pipe(take(1)).subscribe({
                    next: () => {
                      // Limpiar drawerRef$ cuando el drawer se cierre
                      if (this.maestrosUtilsService.drawerRef$.value === drawerRef) {
                        this.maestrosUtilsService.drawerRef = undefined;
                      }
                    }
                  });
                } catch (error) {
                  console.error('Error al acceder al componente del drawer:', error);
                }
              },
              error: (error: any) => {
                console.error('Error al abrir el drawer:', error);
              }
            });
          } catch (error) {
            console.error('Error al crear el drawer:', error);
          }
        },
        error: (error: any) => {
          console.error('Error al obtener información de pantalla:', error);
        }
      });
  }

  // Construir columnas completas con opciones desde GlobalValuesService
  private async buildCompletePartnerColumns(): Promise<TableColumn[]> {
    return new Promise((resolve) => {
      combineLatest({
        partnerTypes: this.globalValuesService.partnerTypes$,
        states: this.globalValuesService.states$,
        countries: this.globalValuesService.countries$,
        currencies: this.globalValuesService.currencies$,
        companies: this.globalValuesService.isPartnerReady$,
      })
        .pipe(take(1))
        .subscribe(({ partnerTypes, states, countries, currencies, companies }) => {
          const companiesList = this.globalValuesService.partnerlist.filter((p: Partner) => p.isCompany);

          const columns: TableColumn[] = [
            {
              name: 'ID',
              pathData: { name: 'id', type: 'number' },
              noVisibleInTable: true,
            },
            {
              name: 'Avatar',
              pathData: { name: 'avatar', type: 'image' },
              width: '65px',
            },
            {
              name: 'Nombre y Apellidos',
              pathData: { name: 'name', type: 'text', displayFn: getPartnerDisplayName },
              width: '250px',
              validators: [
                {
                  error: 'required',
                  function: Validators.required,
                  message: 'El Nombre es requerido',
                },
              ],
            },
            {
              name: 'Apellidos',
              pathData: { name: 'surName', type: 'text' },
              width: '250px',
              noVisibleInTable: true,
            },
            {
              name: 'Tipo de Contacto',
              pathData: { name: 'partnerTypeId', type: 'select', nestedPath: 'name' },
              width: '180px',
              filterable: true,
              optionList: partnerTypes.filter((pt: any) => pt.id !== 4) || [],
              validators: [
                {
                  error: 'required',
                  function: Validators.required,
                  message: 'Tipo de contacto es requerido',
                },
              ],
            },
            {
              name: 'Cargo',
              pathData: { name: 'position', type: 'text' },
              width: '150px',
            },
            {
              name: 'Empresa Relacionada',
              pathData: { name: 'parentId', type: 'avatar-select', nestedPath: 'name' },
              width: '220px',
              filterable: true,
              optionList: companiesList || [],
            },
            {
              name: 'Estado',
              pathData: { name: 'stateId', type: 'select', nestedPath: 'name' },
              width: '150px',
              filterable: true,
              optionList: states || [],
              validators: [
                {
                  error: 'required',
                  function: Validators.required,
                  message: 'El Estado es requerido',
                },
              ],
            },
            {
              name: 'Es Empresa',
              pathData: { name: 'isCompany', type: 'boolean' },
              width: '120px',
              noVisibleInTable: false,
              checkboxOnTable: true,
              filterable: true,
            },
            {
              name: 'Es Empleado',
              pathData: { name: 'employee', type: 'boolean' },
              width: '120px',
              noVisibleInTable: false,
              checkboxOnTable: true,
              filterable: true,
            },
            {
              name: 'Sitio Web',
              pathData: { name: 'website', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'VAT',
              pathData: { name: 'vat', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Notas',
              pathData: { name: 'comment', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Email',
              pathData: { name: 'email', type: 'text' },
              width: '180px',
              validators: [
                {
                  error: 'required',
                  function: Validators.required,
                  message: 'Correo electrónico es requerido',
                },
                {
                  function: Validators.pattern(
                    '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'
                  ),
                  message: 'El email debe ser válido',
                  error: 'pattern',
                },
              ],
            },
            {
              name: 'Email de facturación',
              pathData: { name: 'billingEmail', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
              validators: [
                {
                  function: Validators.pattern(
                    '^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'
                  ),
                  message: 'El email debe ser válido',
                  error: 'pattern',
                },
              ],
            },
            {
              name: 'Teléfono',
              pathData: { name: 'phone', type: 'text' },
              width: '130px',
            },
            {
              name: 'Móvil',
              pathData: { name: 'mobile', type: 'text' },
              width: '130px',
            },
            {
              name: 'Dirección',
              pathData: { name: 'street', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Dirección Adicional',
              pathData: { name: 'street2', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Ciudad',
              pathData: { name: 'city', type: 'text' },
              width: '150px',
            },
            {
              name: 'Código Postal',
              pathData: { name: 'zip', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'País',
              pathData: { name: 'countryId', type: 'select', nestedPath: 'name' },
              width: '150px',
              optionList: countries || [],
            },
            {
              name: 'Latitud',
              pathData: { name: 'latitude', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Longitud',
              pathData: { name: 'longitude', type: 'text' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Divisa',
              pathData: { name: 'currencyId', type: 'select', nestedPath: 'name' },
              width: '120px',
              optionList: currencies || [],
            },
            {
              name: 'Activo',
              pathData: { name: 'isActive', type: 'boolean' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'En lista negra',
              pathData: { name: 'isBlacklisted', type: 'boolean' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Documentos',
              pathData: { name: 'documents', type: 'multi-select' },
              width: '150px',
              noVisibleInTable: true,
            },
            {
              name: 'Habilitado para cargas IMO',
              pathData: { name: 'imo', type: 'boolean' },
              width: '180px',
              checkboxOnTable: true,
            },
          ];

          resolve(columns);
        });
    });
  }
}
